import { Pipe, PipeTransform } from '@angular/core';

/**
 * Exibe o tempo relativo de uma postagem em pt-BR, no formato:
 * "Publicado agora", "Publicado há 5 min", "Publicado há 3 h", "Publicado há 2 d",
 * "Publicado há 1 sem", "Publicado há 2 meses", "Publicado há 1 ano".
 *
 * Nunca mostra data/hora exata — isso também contorna o descasamento de
 * timezone entre API e banco (o importante é o delta, não o horário absoluto).
 *
 * Parsing: datas .NET costumam ser serializadas SEM offset (ex.: "2026-08-11T11:33:00").
 * Nesse caso assumimos UTC (append "Z"), evitando que o navegador interprete como
 * horário local e a conta do delta saia errada.
 */
@Pipe({ name: 'timeAgo' })
export class TimeAgoPipe implements PipeTransform {
  transform(value: string | Date | null | undefined): string {
    if (!value) return '';
    const date = TimeAgoPipe.parse(value);
    if (isNaN(date.getTime())) return '';

    let diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 0) diffSec = 0; // datas "no futuro" (clock skew) -> "agora"

    const MIN = 60, HOUR = 3600, DAY = 86400, WEEK = 604800;
    if (diffSec < 45) return 'Publicado agora';

    let rel: string;
    if (diffSec < HOUR) rel = `${Math.floor(diffSec / MIN)} min`;
    else if (diffSec < DAY) rel = `${Math.floor(diffSec / HOUR)} h`;
    else if (diffSec < WEEK) rel = `${Math.floor(diffSec / DAY)} d`;
    else if (diffSec < DAY * 30) rel = `${Math.floor(diffSec / WEEK)} sem`;
    else {
      const months = Math.floor(diffSec / (DAY * 30));
      if (months < 12) {
        rel = `${months} ${months === 1 ? 'mês' : 'meses'}`;
      } else {
        const years = Math.floor(months / 12);
        rel = `${years} ${years === 1 ? 'ano' : 'anos'}`;
      }
    }
    return `Publicado há ${rel}`;
  }

  private static parse(value: string | Date): Date {
    if (value instanceof Date) return value;
    let s = String(value).trim();
    // Tem horário mas NÃO tem timezone (Z ou +hh:mm / -hh:mm)? Assume UTC.
    const hasTime = /\d{2}:\d{2}/.test(s);
    const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
    if (hasTime && !hasTz) s += 'Z';
    return new Date(s);
  }
}
